import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Calendar, ClipboardList, User, Heart, ChevronRight, LogOut, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { LastTriageData } from "./LastTriageData";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface PatientNavigationProps {
  className?: string;
  showLastTriage?: boolean;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
}

export function PatientNavigation({ 
  className, 
  showLastTriage = true
}: PatientNavigationProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, logout } = useAuth();
  const [isMobile, setIsMobile] = React.useState(false);

  // Navigation items
  const navItems: NavItem[] = [
    { name: "Dashboard", href: "/patient/dashboard", icon: Home },
    { name: "Appointments", href: "/patient/appointments", icon: Calendar },
    { name: "Health Records", href: "/patient/records", icon: ClipboardList },
    { name: "Triage", href: "/patient/triage", icon: Heart },
    { name: "Profile", href: "/patient/profile", icon: User },
  ];

  // Check if mobile on mount and when window resizes
  React.useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Generate breadcrumb items based on current path
  const getBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(Boolean);
    
    // Skip the first "patient" segment in breadcrumbs display
    const relevantPaths = paths[0] === "patient" ? paths.slice(1) : paths;
    
    if (relevantPaths.length === 0) return [];
    
    return relevantPaths.map((path, index) => {
      // Build the href for this breadcrumb
      const href = `/${paths.slice(0, index + (paths[0] === "patient" ? 2 : 1)).join('/')}`;
      
      // Format the path name (capitalize first letter)
      const name = path.charAt(0).toUpperCase() + path.slice(1);
      
      return { name, href };
    });
  };
  
  const breadcrumbs = getBreadcrumbs();

  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return profile?.email || user?.email || "Patient";
  };

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`;
    }
    const emailInitial = profile?.email?.[0] || user?.email?.[0];
    return emailInitial?.toUpperCase() || "P";
  };

  const handleSignOut = async () => {
    await logout();
    navigate("/auth/login"); // Redirect to login after sign out
  };
  
  return (
    <div className={cn("flex flex-col", className)}>
      {/* Top navigation bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-4 bg-white border-b">
        {/* Left side: Logo and Breadcrumbs */}
        <div className="hidden md:flex items-center gap-3">
          {/* Logo for desktop view */}
          <Link to="/patient/dashboard" className="h-8 mr-2">
            <img src="/clinicaflow-logo.svg" alt="CLINICAFLOW" className="h-full" />
          </Link>
          
          {/* Breadcrumbs */}
          <div className="flex items-center text-sm text-muted-foreground">
            <Link to="/patient/dashboard" className="hover:text-foreground">
              Home
            </Link>
            {breadcrumbs.map((crumb, index) => (
              <div key={crumb.href} className="flex items-center">
                <ChevronRight className="h-4 w-4 mx-1" />
                <Link 
                  to={crumb.href} 
                  className={cn(
                    "hover:text-foreground",
                    index === breadcrumbs.length - 1 ? "font-medium text-foreground" : ""
                  )}
                >
                  {crumb.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
        
        {/* Logo for mobile view */}
        <div className="md:hidden flex items-center">
          <Link to="/patient/dashboard" className="h-8">
            <img src="/clinicaflow-logo.svg" alt="CLINICAFLOW" className="h-full" />
          </Link>
        </div>

        {/* Right side: Navigation tabs and User Profile Dropdown */}
        <div className="flex items-center gap-2 w-full md:w-auto mt-4 md:mt-0">
          {/* Navigation tabs - centered on mobile, right-aligned on desktop */} 
          <div className="flex flex-grow justify-center md:justify-end overflow-x-auto pb-1 md:pb-0 gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href || 
                              (item.href !== "/patient/dashboard" && location.pathname.startsWith(item.href));
              
              return (
                <Button
                  key={item.name}
                  variant={isActive ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "flex items-center gap-1 shrink-0",
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                  )}
                  asChild
                >
                  <Link to={item.href}>
                    <item.icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{item.name}</span>
                  </Link>
                </Button>
              );
            })}
          </div>

          {/* User Profile Dropdown */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={profile?.avatar_url || undefined} alt={getDisplayName()} />
                    <AvatarFallback>{getInitials()}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{getDisplayName()}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {profile?.email || user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/patient/profile">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/patient/settings"> 
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      
      {/* Mobile last triage data - only visible on small screens */} 
      {showLastTriage && (
        <div className="md:hidden px-4 py-2"> 
          <LastTriageData 
            variant="sidebar" 
            className="bg-white border rounded-md shadow-sm"
            showTriageButton={true}
          />
        </div>
      )}
    </div>
  );
}
